import { offlineDb, type MutationQueueItem, type SyncFailureRecord } from './db'
import { isOnlineNow, isServerReachable } from './connectivity'
import { store } from '../state/store'
import { syncAjax } from '../ajax/SyncAjax'

const MAX_STORED_FAILURES = 200
const UPLOAD_BATCH_SIZE = 50
const MAX_MESSAGE_CHARS = 1000
const MAX_PAYLOAD_CHARS = 8000

let isUploading = false

export async function recordSyncFailure(
  item: MutationQueueItem,
  httpStatus: number | undefined,
  message: string | undefined,
  outcome: SyncFailureRecord['outcome'],
  attempt: { mode: 'batch' | 'single'; batchSize: number },
): Promise<void> {
  const { expiresAt } = store.getState().authReducer
  const now = Date.now()

  // Note: context is assembled field by field so that nothing from the auth state beyond the expiry
  // time can reach the log, the tokens especially.
  const context = {
    queuedAt: new Date(item.createdAt).toISOString(),
    attempt: attempt.mode,
    batchSize: attempt.batchSize,
    tokenExpiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    tokenExpired: expiresAt ? expiresAt <= now : null,
    serverReachable: isServerReachable(),
    navigatorOnLine: navigator.onLine,
    serverErrorSince: item.serverErrorSince ? new Date(item.serverErrorSince).toISOString() : null,
  }

  await offlineDb.syncFailures.add({
    occurredAt: now,
    clientId: item.clientId,
    entity: item.entity,
    operation: item.operation,
    shiftId: item.shiftId,
    httpStatus,
    message: message?.slice(0, MAX_MESSAGE_CHARS),
    outcome,
    context: JSON.stringify(context),
    payload: JSON.stringify(item.data ?? null).slice(0, MAX_PAYLOAD_CHARS),
  })

  const excess = (await offlineDb.syncFailures.count()) - MAX_STORED_FAILURES
  if (excess > 0) {
    const oldest = await offlineDb.syncFailures.orderBy('occurredAt').limit(excess).primaryKeys()
    await offlineDb.syncFailures.bulkDelete(oldest)
  }
}

export async function uploadSyncFailures(): Promise<void> {
  if (isUploading || !isOnlineNow()) return
  isUploading = true
  try {
    const pending = await offlineDb.syncFailures.orderBy('occurredAt').limit(UPLOAD_BATCH_SIZE).toArray()
    if (pending.length === 0) return

    const response = await syncAjax.recordFailures(pending.map(({ id: _id, occurredAt, ...failure }) => ({
      ...failure,
      occurredAt: new Date(occurredAt).toISOString(),
    })))
    if (response.success) {
      await offlineDb.syncFailures.bulkDelete(pending.map(p => p.id!))
    }
  } catch {
    return
  } finally {
    isUploading = false
  }
}
